function charloxReviewArchivePage(records, {page=1, perPage=3, rating='', sort='pictures_first', excluded=[]} = {}) {
  const excludedIDs = new Set(excluded);
  const merged = new Map();
  for (const review of records) {
    if (excludedIDs.has(review.uuid) || Number(review.rating)<1 || Number(review.rating)>5) continue;
    merged.set(review.uuid, {...merged.get(review.uuid), ...review});
  }
  const unique = [...merged.values()];
  let filtered = unique.filter(r => !rating || Number(r.rating)===Number(rating));
  const hasPhoto = r => Array.isArray(r.pictures_urls) && r.pictures_urls.length>0;
  if (sort==='with_pictures') filtered=filtered.filter(hasPhoto);
  filtered.sort((a,b) => {
    if(sort==='highest_rating' && a.rating!==b.rating)return b.rating-a.rating;
    if(sort==='lowest_rating' && a.rating!==b.rating)return a.rating-b.rating;
    if(sort==='pictures_first' && hasPhoto(a)!==hasPhoto(b))return Number(hasPhoto(b))-Number(hasPhoto(a));
    return new Date(b.created_at||0)-new Date(a.created_at||0);
  });
  const total=unique.length, pages=Math.max(1,Math.ceil(filtered.length/perPage)), current=Math.min(pages,Math.max(1,page));
  return {reviews:filtered.slice((current-1)*perPage,current*perPage), number_of_reviews:total,
    average_rating:total?unique.reduce((s,r)=>s+Number(r.rating),0)/total:0,
    histogram:[5,4,3,2,1].map(rating=>{const frequency=unique.filter(r=>Number(r.rating)===rating).length;return {rating,frequency,percentage:total?frequency/total*100:0};}),
    pagination:{current_page:current,total_pages:pages}};
}
if (!customElements.get('charlox-review-pages')) customElements.define('charlox-review-pages', class extends HTMLElement {
  connectedCallback() {
    if (this.started) return;
    this.started = true;
    this.section = this.closest('[data-charlox-reviews]');
    this.strings = JSON.parse(this.querySelector('[data-review-strings]').textContent);
    this.hasArchive = this.dataset.archiveKnown === 'true';
    this.archive = JSON.parse(this.querySelector('[data-review-archive]')?.textContent || '[]');
    if(!Array.isArray(this.archive))this.archive=[];
    this.reviewExclusions=JSON.parse(this.querySelector('[data-review-exclusions]')?.textContent || '[]');
    if(!Array.isArray(this.reviewExclusions))this.reviewExclusions=[];
    this.page = 1;
    this.rating = '';
    this.sort = this.section.dataset.photosFirst === 'true' ? 'pictures_first' : 'created_at';
    this.cache = new Map();
    this.build();
    this.load();
    this.nativeObserver = new MutationObserver(() => this.updateWriteButton());
    this.nativeObserver.observe(this.section.querySelector('.charlox-native-reviews'), {childList:true,subtree:true});
    this.updateWriteButton();
  }
  disconnectedCallback() {
    this.controller?.abort();
    this.nativeObserver?.disconnect();
  }
  node(tag, cls, text) {
    const el = document.createElement(tag);
    if (cls) el.className = cls;
    if (text !== undefined) el.textContent = text;
    return el;
  }
  label(key, values = {}) {
    let text = this.strings[key] || '';
    Object.entries(values).forEach(([key,value]) => {text = text.replaceAll(`%${key}%`, String(value));});
    return text;
  }
  button(label, cls, callback) {
    const b = this.node('button', cls, label);
    b.type = 'button'; b.addEventListener('click', callback); return b;
  }
  build() {
    const head = this.node('div','cr-header');
    const intro = this.node('div','cr-intro');
    intro.append(this.node('h2','cr-title',this.strings.title));
    this.summary = this.node('div','cr-summary');
    intro.append(this.summary);
    this.write = this.button(this.strings.write,'cr-write',() => this.section.querySelector('[data-testid="write-review-button"]')?.click());
    intro.append(this.write);
    this.histogram = this.node('div','cr-histogram');
    this.histogram.setAttribute('aria-label',this.strings.rating);
    const controls = this.node('div','cr-controls');
    const sortLabel = this.node('label','cr-select-label',this.strings.sort);
    this.sortSelect = this.node('select','cr-select');
    for (const [value,key] of [['pictures_first','photos'],['created_at','recent'],['highest_rating','highest'],['lowest_rating','lowest'],['with_pictures','only_photos'],...((this.hasArchive||this.archive.length||this.reviewExclusions.length)?[]:[['most_helpful','helpful']])]) {
      const option = this.node('option','',this.strings[key]);option.value=value;this.sortSelect.append(option);
    }
    this.sortSelect.value = this.sort;
    this.sortSelect.addEventListener('change',() => {this.sort=this.sortSelect.value;this.page=1;this.load();});
    sortLabel.append(this.sortSelect);
    const ratingLabel = this.node('label','cr-select-label',this.strings.rating);
    this.ratingSelect = this.node('select','cr-select');
    for (const value of ['',5,4,3,2,1]) {
      const option=this.node('option','',value?this.label('stars',{rating:value}):this.strings.all);option.value=value;this.ratingSelect.append(option);
    }
    this.ratingSelect.addEventListener('change',() => {this.rating=this.ratingSelect.value;this.page=1;this.load();});
    ratingLabel.append(this.ratingSelect);controls.append(sortLabel,ratingLabel);
    head.append(intro,this.histogram,controls);
    this.list = this.node('div','cr-list');
    this.list.setAttribute('role','list');
    this.list.setAttribute('aria-label',this.strings.title);
    this.status = this.node('p','cr-status');this.status.setAttribute('aria-live','polite');
    const foot=this.node('nav','cr-pagination');foot.setAttribute('aria-label',this.strings.pagination);
    this.previous=this.button('','cr-page-button',()=>this.changePage(-1));
    this.previous.setAttribute('aria-label',this.strings.previous);
    this.next=this.button('','cr-page-button',()=>this.changePage(1));
    this.next.setAttribute('aria-label',this.strings.next);
    this.previous.append(this.arrow(true));this.next.append(this.arrow(false));
    this.pageLabel=this.node('span','cr-page-label');
    foot.append(this.previous,this.pageLabel,this.next);
    this.dialog=this.node('dialog','cr-photo-dialog');
    const close=this.button('×','cr-photo-close',()=>this.dialog.close());close.setAttribute('aria-label',this.strings.close);
    this.photo=this.node('img','cr-photo-full');this.dialog.append(close,this.photo);
    this.dialog.addEventListener('click',event=>{if(event.target===this.dialog)this.dialog.close();});
    this.provenance=this.node('p','cr-provenance',this.strings.provenance||'');this.provenance.hidden=!this.archive.some(r=>r.origin);
    this.append(head,this.provenance,this.list,this.status,foot,this.dialog);
  }
  arrow(back) {
    const svg=document.createElementNS('http://www.w3.org/2000/svg','svg');svg.setAttribute('viewBox','0 0 24 24');svg.setAttribute('aria-hidden','true');
    const path=document.createElementNS(svg.namespaceURI,'path');path.setAttribute('d',back?'M19 12H5m6-6-6 6 6 6':'M5 12h14m-6-6 6 6-6 6');svg.append(path);return svg;
  }
  updateWriteButton() {this.write.disabled=!this.section.querySelector('[data-testid="write-review-button"]');}
  applyArchiveData(data) {
    this.render(data);this.dataset.ready='true';this.section.dataset.reviewPagesReady='true';this.hidden=false;
    document.querySelectorAll('[data-charlox-archive-average]').forEach(n=>{n.textContent=Number(data.average_rating).toLocaleString(document.documentElement.lang,{minimumFractionDigits:1,maximumFractionDigits:1})+'/5';n.hidden=!Number(data.number_of_reviews);});
    document.querySelectorAll('[data-charlox-archive-stars]').forEach(n=>{n.style.setProperty('--rating-percent',String(Number(data.average_rating)*20)+'%');});
    document.querySelectorAll('[data-charlox-archive-count]').forEach(n=>{n.textContent=this.label('count',{count:data.number_of_reviews});});
    document.querySelectorAll('[data-charlox-curated-badge]').forEach(n=>{n.style.display='';n.dataset.reviewEmpty=String(!Number(data.number_of_reviews));});
  }
  async loadArchive() {
    this.controller?.abort();this.controller=new AbortController();const controller=this.controller;
    const localData=charloxReviewArchivePage([...(this.nativeReviews||[]),...this.archive],{page:this.page,perPage:Number(this.section.dataset.visibleReviews)||3,rating:this.rating,sort:this.sort,excluded:this.reviewExclusions});
    this.applyArchiveData(localData);
    if(this.nativeReviews)return;
    const timeout=setTimeout(()=>controller.abort(),6000);
    this.removeAttribute('aria-busy');
    try {
      const list=[];
      for(let page=1;page<=20;page++){
        const params=new URLSearchParams({shop_domain:this.dataset.shop,platform:'shopify',product_id:this.dataset.product,per_page:'100',page:String(page),sort_by:'created_at'});
        const response=await fetch(`https://api.judge.me/reviews/reviews_for_widget?${params}`,{signal:controller.signal,headers:{Accept:'application/json'}});
        if(!response.ok)throw Error('Review service unavailable');
        const data=await response.json();if(!Array.isArray(data.reviews))throw Error('Invalid review data');
        list.push(...data.reviews);
        if(page>=Number(data.pagination?.total_pages||1))break;
      }
      if(this.controller!==controller)return;
      this.nativeReviews=list;
      const data=charloxReviewArchivePage([...this.nativeReviews,...this.archive],{page:this.page,perPage:Number(this.section.dataset.visibleReviews)||3,rating:this.rating,sort:this.sort,excluded:this.reviewExclusions});
      this.applyArchiveData(data);
    }catch(error){
      if(error.name==='AbortError'||this.controller!==controller)return;
      // Local, genuine reviews are already visible if the external service fails.
    }finally{clearTimeout(timeout);if(this.controller===controller)this.removeAttribute('aria-busy');}
  }
  async load() {
    if(this.hasArchive||this.archive.length||this.reviewExclusions.length)return this.loadArchive();
    this.controller?.abort();this.controller=new AbortController();const controller=this.controller;
    const params=new URLSearchParams({shop_domain:this.dataset.shop,platform:'shopify',product_id:this.dataset.product,per_page:this.section.dataset.visibleReviews||'3',page:String(this.page),sort_by:this.sort});
    if(this.rating)params.set('filter_rating',this.rating);
    if(this.sort==='highest_rating'){params.set('sort_by','rating');params.set('sort_dir','desc');}
    if(this.sort==='lowest_rating'){params.set('sort_by','rating');params.set('sort_dir','asc');}
    const key=params.toString();this.setAttribute('aria-busy','true');
    try {
      let data=this.cache.get(key);
      if(!data){const response=await fetch(`https://api.judge.me/reviews/reviews_for_widget?${key}`,{signal:this.controller.signal,headers:{Accept:'application/json'}});if(!response.ok)throw Error('Review service unavailable');data=await response.json();if(!Array.isArray(data.reviews))throw Error('Invalid review data');this.cache.set(key,data);}
      this.render(data);
      this.dataset.ready='true';this.section.dataset.reviewPagesReady='true';
      this.hidden=false;
      document.querySelectorAll('[data-charlox-archive-average]').forEach(n=>{n.textContent=Number(data.average_rating).toLocaleString(document.documentElement.lang,{minimumFractionDigits:1,maximumFractionDigits:1})+'/5';n.hidden=!Number(data.number_of_reviews);});
      document.querySelectorAll('[data-charlox-archive-stars]').forEach(n=>{n.style.setProperty('--rating-percent',String(Number(data.average_rating)*20)+'%');});
      document.querySelectorAll('[data-charlox-archive-count]').forEach(n=>{n.textContent=this.label('count',{count:data.number_of_reviews});});
      document.querySelectorAll('[data-charlox-curated-badge]').forEach(n=>{n.style.display='';n.dataset.reviewEmpty=String(!Number(data.number_of_reviews));});
    } catch(error) {
      if(error.name==='AbortError')return;
      if(!this.dataset.ready){this.hidden=true;delete this.section.dataset.reviewPagesReady;}
      else this.status.textContent=this.strings.retry;
    } finally {if(this.controller===controller)this.removeAttribute('aria-busy');}
  }
  render(data) {
    this.dataset.empty=String(!Number(data.number_of_reviews));
    this.pages=Math.max(1,Number(data.pagination?.total_pages)||1);
    this.page=Number(data.pagination?.current_page)||1;
    this.summary.replaceChildren();
    const average=this.node('strong','cr-average',Number(data.average_rating).toLocaleString(document.documentElement.lang,{minimumFractionDigits:1,maximumFractionDigits:1}));
    const detail=this.node('div','cr-summary-detail');detail.append(this.starRow(Number(data.average_rating)),this.node('span','',this.label('count',{count:data.number_of_reviews})));
    this.summary.append(average,detail);
    this.histogram.replaceChildren();
    for(const row of data.histogram||[]){
      const button=this.button('','cr-histogram-row',()=>{this.rating=this.rating===String(row.rating)?'':String(row.rating);this.ratingSelect.value=this.rating;this.page=1;this.load();});
      button.setAttribute('aria-label',this.label('rating_count',{rating:row.rating,count:row.frequency}));
      button.setAttribute('aria-pressed',String(this.rating===String(row.rating)));
      const label=this.node('span','cr-histogram-label',`${row.rating} ★`),track=this.node('span','cr-histogram-track'),fill=this.node('span','cr-histogram-fill');fill.style.width=`${Math.max(0,Math.min(100,Number(row.percentage)||0))}%`;track.append(fill);button.append(label,track,this.node('span','cr-histogram-count',String(row.frequency)));this.histogram.append(button);
    }
    this.list.replaceChildren();
    data.reviews.forEach(review=>this.list.append(this.review(review)));
    this.status.textContent=Number(data.number_of_reviews)?(data.reviews.length?'':this.strings.empty):(this.strings.no_reviews||'');
    this.previous.disabled=this.page<=1;this.next.disabled=this.page>=this.pages;
    this.pageLabel.textContent=this.label('page',{current:this.page,total:this.pages});
    this.ratingSelect.value=this.rating;
  }
  starRow(rating) {
    const filled=Math.max(0,Math.min(5,Math.round(rating)));
    const stars=this.node('span','cr-stars','★'.repeat(filled)+'☆'.repeat(5-filled));stars.setAttribute('role','img');stars.setAttribute('aria-label',this.label('stars',{rating}));return stars;
  }
  review(review) {
    const card=this.node('article','cr-card');card.setAttribute('role','listitem');card.dataset.reviewUuid=review.uuid;
    const top=this.node('div','cr-card-top');top.append(this.starRow(review.rating));
    const time=this.node('time','cr-date');time.dateTime=review.created_at||'';const date=new Date(review.created_at);time.textContent=Number.isNaN(date.valueOf())?'':date.toLocaleDateString(document.documentElement.lang,{day:'2-digit',month:'2-digit',year:'numeric'});top.append(time);card.append(top);
    const person=this.node('div','cr-person');person.append(this.node('span','cr-avatar',review.reviewer_initial||''),this.node('span','cr-name',review.reviewer_name||review.reviewer_initial||''));card.append(person);
    if(review.title)card.append(this.node('h3','cr-card-title',review.title));
    const parsed=new DOMParser().parseFromString(review.body_html||'','text/html');parsed.querySelectorAll('script,style').forEach(n=>n.remove());parsed.querySelectorAll('br').forEach(b=>b.replaceWith('\n'));parsed.querySelectorAll('p').forEach(p=>p.append('\n'));
    const text=parsed.body.textContent.trim();
    if(text){const body=this.node('p','cr-body',text);card.append(body);if(text.length>260){body.classList.add('cr-body--collapsed');const more=this.button(this.strings.more,'cr-read-more',()=>{const collapsed=body.classList.toggle('cr-body--collapsed');more.textContent=collapsed?this.strings.more:this.strings.less;more.setAttribute('aria-expanded',String(!collapsed));});more.setAttribute('aria-expanded','false');card.append(more);}}
    const media=this.node('div','cr-media');
    for(const picture of review.pictures_urls||[]){
      const raw=picture.original||picture.huge||picture.compact;const small=picture.compact||picture.small||raw;
      if(!raw||!small)continue;
      const button=this.button('','cr-photo',()=>{this.photo.src=raw;this.photo.alt=this.label('photo',{name:review.reviewer_name||''});this.dialog.showModal();});button.setAttribute('aria-label',this.label('photo',{name:review.reviewer_name||''}));
      const image=this.node('img');image.src=small;image.alt='';image.loading='lazy';image.width=128;image.height=128;button.append(image);media.append(button);
    }
    if(media.childElementCount)card.append(media);
    return card;
  }
  changePage(direction) {
    if(this.getAttribute('aria-busy')==='true')return;
    this.page=Math.max(1,Math.min(this.pages,this.page+direction));this.load();
  }
});
